/**
 * Sound Effects Manager
 * Generates synthetic sounds using Web Audio API
 */

class SoundManager {
  private audioContext: AudioContext | null = null;
  private soundEnabled: boolean = true;

  constructor() {
    // Create audio context on first user interaction
    if (typeof window !== 'undefined') {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  /**
   * Ensure audio context is resumed (required for autoplay policies)
   */
  private async ensureAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('🔊 Audio context created:', this.audioContext.state);
    }
    if (this.audioContext.state === 'suspended') {
      console.log('🔊 Resuming suspended audio context...');
      await this.audioContext.resume();
      console.log('🔊 Audio context resumed:', this.audioContext.state);
    }
    return this.audioContext;
  }

  /**
   * Play a hop sound (short boing)
   */
  async playHop() {
    if (!this.soundEnabled) return;
    try {
      await this.ensureAudioContext();
      if (!this.audioContext) return;

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(400, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, this.audioContext.currentTime + 0.1);

      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.1);
    } catch (error) {
      console.log('Sound playback failed:', error);
    }
  }

  /**
   * Play celebration sound (victory fanfare)
   */
  async playCelebration() {
    if (!this.soundEnabled) return;
    try {
      await this.ensureAudioContext();
      if (!this.audioContext) return;

      // Play a series of ascending notes
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C, E, G, C (major chord)

      notes.forEach((frequency, index) => {
        setTimeout(() => {
          this.playNote(frequency, 0.3, 0.2);
        }, index * 150);
      });

      // Add a final triumphant note
      setTimeout(() => {
        this.playNote(1046.50, 0.5, 0.4);
      }, 600);

    } catch (error) {
      console.log('Celebration sound failed:', error);
    }
  }

  /**
   * Play sparkle sound (for confetti/effects)
   */
  async playSparkle() {
    if (!this.soundEnabled) return;
    try {
      await this.ensureAudioContext();
      if (!this.audioContext) return;

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(2000, this.audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(4000, this.audioContext.currentTime + 0.05);

      gainNode.gain.setValueAtTime(0.15, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.05);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.05);
    } catch (error) {
      console.log('Sparkle sound failed:', error);
    }
  }

  /**
   * Play a single note
   */
  private playNote(frequency: number, volume: number, duration: number) {
    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration);

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  /**
   * Play continuous sparkles (for celebration effects)
   */
  async playSparkleSequence(count: number = 10, interval: number = 100) {
    if (!this.soundEnabled) return;
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        this.playSparkle();
      }, i * interval);
    }
  }

  /**
   * Enable sound effects
   */
  enableSound(): void {
    this.soundEnabled = true;
    console.log('🔊 Sound effects enabled');
  }

  /**
   * Disable sound effects
   */
  disableSound(): void {
    this.soundEnabled = false;
    console.log('🔇 Sound effects disabled');
  }

  /**
   * Check if sound is enabled
   */
  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  /**
   * Toggle sound on/off
   */
  toggleSound(): boolean {
    this.soundEnabled = !this.soundEnabled;
    console.log(`🔊 Sound effects ${this.soundEnabled ? 'enabled' : 'disabled'}`);
    return this.soundEnabled;
  }
}

// Export singleton instance
export const soundManager = new SoundManager();
